/**
 * Edit systems item component
 */

// React
import React, { FC, useLayoutEffect } from 'react';

// Libraries
import { Controller, useWatch } from 'react-hook-form';

// Material
import { Box, useTheme } from '@mui/material';

// Components
import AppRadio from 'components/AppRadio';
import AppDate from 'components/AppDate';
import { AppCheckbox } from 'components/AppCheckbox';

// Common styles
import {
  ContentSystem,
  ContentSystemItem,
  RadioGroupContainer,
  SystemIcon,
  TypographySystem,
  WrapperIcon,
} from 'styles';

// Utils
import { DataEditSystem } from 'utils/types';
import { defaultRadioIsUsed, formatDate } from 'utils/helper/common';
import { AWS_S3, STATUS_SYSTEM_TEXT, StatusKey } from 'utils/constants';

// Interface
interface EditSystemItemProps {
  index: number;
  field: DataEditSystem;
  control: any;
  setValue: any;
  usageValue: number;
  errors: any;
  handleUsageChange: (_index: number, _value: number) => void;
}

// Component
const EditSystemItem: FC<EditSystemItemProps> = ({
  index,
  field,
  control,
  usageValue,
  errors,
  setValue,
  handleUsageChange,
}) => {
  const theme = useTheme();

  const checkStatusActive = (status: string): boolean => {
    return status === StatusKey.REQUESTED || status === StatusKey.CANCELED;
  };

  const defaultCheckbox = (status: string | undefined): boolean => {
    return status === StatusKey.CANCELED;
  };

  const defaultCheckDate = (status: string): number => {
    if (usageValue !== null) {
      return usageValue;
    }
    return defaultRadioIsUsed(status);
  };

  const isApproved = useWatch({ control, name: `systems.${index}.isApproved` });
  const isUsed = useWatch({ control, name: `systems.${index}.is_used` });
  const startDate = useWatch({ control, name: `systems.${index}.start_date` });
  const createdAt = useWatch({ control, name: `systems.${index}.created_at` });
  useLayoutEffect(() => {
    if (isApproved) {
      setValue(`systems.${index}.start_date`, null);
      setValue(`systems.${index}.end_date`, null);
    }
    if (!startDate && !isApproved && isUsed) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      setValue(`systems.${index}.start_date`, today);
    }
  }, [isApproved]);

  return (
    <Box key={`${field?.id} ${index}`}>
      <SystemIcon>
        <WrapperIcon src={`${AWS_S3.BASE_URL}/${field?.icon}`} alt="Image content system" />
        <TypographySystem sx={{ fontWeight: 'bold', color: field.color }} variant="h4">
          {field.title}
        </TypographySystem>
      </SystemIcon>
      <Box display="flex">
        <Box
          sx={{
            width: '300px',
            [theme.breakpoints.down('sm')]: {
              width: '0',
            },
          }}
        ></Box>
        <ContentSystem>
          <ContentSystemItem>
            <TypographySystem>現状ステータス</TypographySystem>
            <TypographySystem>{STATUS_SYSTEM_TEXT[field?.status]}</TypographySystem>
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>利用 ON/OFF</TypographySystem>
            <Controller
              name={`systems.${index}.is_used`}
              control={control}
              render={({ field: radioField }) => (
                <RadioGroupContainer
                  {...radioField}
                  value={
                    radioField.value === null ? defaultRadioIsUsed(field?.status) : radioField.value
                  }
                  onChange={(e) => handleUsageChange(index, Number(e.target.value))}
                >
                  <AppRadio value={1} label={'ON'} />
                  <AppRadio value={0} label={'OFF'} />
                </RadioGroupContainer>
              )}
            />
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>非承認</TypographySystem>
            <Controller
              name={`systems.${index}.isApproved`}
              control={control}
              render={({ field: checkboxField }) => (
                <AppCheckbox
                  disabled={!checkStatusActive(field?.status || '')}
                  {...checkboxField}
                  checked={checkboxField.value ?? defaultCheckbox(field?.status)}
                />
              )}
            />
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>申込日</TypographySystem>
            <TypographySystem>{formatDate(createdAt)}</TypographySystem>
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>開始日</TypographySystem>
            <AppDate
              name={`systems.${index}.start_date`}
              disabled={defaultCheckDate(field?.status) === 0 || isApproved}
              control={control}
              helperText={errors?.systems?.[index]?.start_date?.message}
            />
          </ContentSystemItem>
          <ContentSystemItem>
            <TypographySystem>終了日</TypographySystem>

            <AppDate
              name={`systems.${index}.end_date`}
              disabled={defaultCheckDate(field?.status) === 0 || isApproved}
              control={control}
              helperText={errors?.systems?.[index]?.end_date?.message}
            />
          </ContentSystemItem>
        </ContentSystem>
      </Box>
    </Box>
  );
};

export default EditSystemItem;
